import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  useLinkDevices,
  useRegenerateActivationCode,
  useRevokeTerminal,
  type TerminalStatus,
} from '@/hooks/use-terminals';
import { useT } from '@/i18n/use-t';
import { ActivationCodeAlert, StatusBadge, extractError, relative } from './terminals-list';

/**
 * Terminal Management → Link devices (RL-449).
 *
 * A Link is activated, re-activated and revoked exactly like a till, so the
 * actions here are the till page's: a new activation code (re-activation, e.g.
 * a replaced server) and revoke. What differs is what is shown: the store it
 * serves and that store's connector sources — as summaries; their connection
 * details are authored under Settings → Integrations and never shown here.
 */
export function LinkDevicesPage() {
  const t = useT('admin_ui');
  const [status, setStatus] = useState<TerminalStatus | 'any'>('any');
  const { data, isLoading } = useLinkDevices(status === 'any' ? undefined : status);
  const revoke = useRevokeTerminal();
  const regen = useRegenerateActivationCode();
  const [code, setCode] = useState<{ activationCode: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('terminals.link.title', 'Link devices')}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'terminals.link.subtitle',
              'In-store connector hosts. Each serves one location and its connector sources.',
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Select
            aria-label={t('terminals.field.status', 'Status')}
            value={status}
            onChange={(e) => setStatus(e.target.value as TerminalStatus | 'any')}
            className="w-40"
          >
            <option value="any">{t('terminals.list.statusAny', 'All statuses')}</option>
            <option value="provisioned">
              {t('terminals.list.statusProvisioned', 'Provisioned')}
            </option>
            <option value="active">{t('terminals.list.statusActive', 'Active')}</option>
            <option value="revoked">{t('terminals.list.statusRevoked', 'Revoked')}</option>
          </Select>
          <Button asChild variant="outline">
            <Link to="/terminals">{t('terminals.backToTerminals', 'Back to terminals')}</Link>
          </Button>
          <Button asChild>
            <Link to="/terminals/new?kind=link">
              {t('terminals.link.register', 'Register Link device')}
            </Link>
          </Button>
        </div>
      </header>

      {code !== null && (
        <ActivationCodeAlert
          activationCode={code.activationCode}
          expiresAt={code.expiresAt}
          onClose={() => setCode(null)}
        />
      )}

      <div className="rounded-md border bg-background">
        <Table>
          <THead>
            <TR>
              <TH>{t('terminals.field.code', 'Code')}</TH>
              <TH>{t('terminals.field.name', 'Name')}</TH>
              <TH>{t('terminals.field.location', 'Location')}</TH>
              <TH>{t('terminals.link.colSources', 'Connector sources')}</TH>
              <TH>{t('terminals.field.status', 'Status')}</TH>
              <TH>{t('terminals.list.colLastHeartbeat', 'Last heartbeat')}</TH>
              <TH className="w-48" />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={7} className="py-6 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {data?.devices.length === 0 && (
              <TR>
                <TD colSpan={7} className="py-6 text-center text-muted-foreground">
                  {t(
                    'terminals.link.empty',
                    'No Link devices. Click "Register Link device" to add one.',
                  )}
                </TD>
              </TR>
            )}
            {data?.devices.map((d) => (
              <TR key={d.id}>
                <TD className="font-mono text-xs">{d.code}</TD>
                <TD className="font-medium">{d.displayName}</TD>
                <TD className="text-sm">
                  {d.location === null ? '—' : `${d.location.code} — ${d.location.name}`}
                </TD>
                <TD>
                  {d.connectorSources.length === 0 ? (
                    <span className="text-xs text-muted-foreground">
                      {t('terminals.link.noSources', 'none configured')}
                    </span>
                  ) : (
                    <ul className="space-y-1">
                      {d.connectorSources.map((s) => (
                        <li key={s.id} className="flex flex-wrap items-center gap-1 text-sm">
                          <span>{s.name}</span>
                          {s.profile === 'ip-4' && (
                            <Badge variant="outline">
                              {t('settings.integrations.ip4.profileLabel', 'document feed')}
                            </Badge>
                          )}
                          {!s.enabled && (
                            <Badge variant="outline">
                              {t('terminals.link.sourceDisabled', 'disabled')}
                            </Badge>
                          )}
                          {s.exportEnabled && (
                            <Badge variant="outline">
                              {t('terminals.link.sourceExport', 'export on')}
                            </Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </TD>
                <TD>
                  <StatusBadge status={d.status} isOnline={d.isOnline} />
                </TD>
                <TD className="text-xs text-muted-foreground">{relative(d.lastHeartbeatAt, t)}</TD>
                <TD className="space-x-2 text-right">
                  {d.status !== 'revoked' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={regen.isPending}
                        onClick={() => {
                          setError(null);
                          regen
                            .mutateAsync(d.id)
                            .then((c) => setCode(c))
                            .catch((err: unknown) => setError(extractError(err, t)));
                        }}
                      >
                        {t('terminals.list.newCode', 'New code')}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" disabled={revoke.isPending}>
                            {t('terminals.list.revoke', 'Revoke')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t(
                                'terminals.link.revokeTitle',
                                'Revoke Link device "{code}"?',
                              ).replace('{code}', d.code)}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {t(
                                'terminals.link.revokeHelp',
                                'Its token stops working immediately: it can no longer receive its configuration or connector credentials. This cannot be undone — register a new Link device for this location to replace it.',
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className={cn(buttonVariants({ variant: 'destructive' }))}
                              onClick={() => {
                                setError(null);
                                revoke
                                  .mutateAsync(d.id)
                                  .catch((err: unknown) => setError(extractError(err, t)));
                              }}
                            >
                              {t('terminals.link.revokeConfirm', 'Revoke Link device')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
